import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { 
  providerConfigModel, 
  createProviderConfig, 
  validateProviderConfig, 
  formatProviderConfigForDisplay,
  predefinedProviders,
  getActiveProviders,
  getProviderByCode,
  getProvidersByPropertyType,
  getProviderStatistics
} from '../data/providerConfigModel';

// CRUD Operations for Provider Configurations

// Create a new provider configuration
export const createProviderConfigDocument = async (providerData, userId) => {
  try {
    console.log('🏗️ Creating provider configuration document');
    console.log('👤 User ID:', userId);
    console.log('🔧 Provider data:', providerData);
    
    const providerConfig = createProviderConfig({
      ...providerData,
      createdBy: userId,
      updatedBy: userId
    });
    
    console.log('📋 Created provider config object:', providerConfig);
    
    const validation = validateProviderConfig(providerConfig);
    console.log('✅ Validation result:', validation);
    
    if (!validation.isValid) {
      console.log('❌ Validation failed:', validation.errors);
      throw new Error(`Validation failed: ${Object.values(validation.errors).join(', ')}`);
    }
    
    console.log('💾 Saving to Firestore...');
    const docRef = await addDoc(collection(db, providerConfigModel.collection), providerConfig);
    
    console.log('✅ Provider configuration created successfully with ID:', docRef.id);
    return {
      success: true,
      id: docRef.id,
      data: providerConfig
    };
    
  } catch (error) {
    console.error('❌ Error creating provider configuration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get all provider configurations
export const getAllProviderConfigs = async () => {
  try {
    console.log('📖 Fetching all provider configurations...');
    
    const q = query(
      collection(db, providerConfigModel.collection),
      orderBy('settings.priority', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const providerConfigs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('✅ Fetched provider configurations:', providerConfigs.length);
    return {
      success: true,
      data: providerConfigs
    };
    
  } catch (error) {
    console.error('❌ Error fetching provider configurations:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Get provider configuration by ID
export const getProviderConfigById = async (providerId) => {
  try {
    console.log('📖 Fetching provider configuration by ID:', providerId);
    
    const docRef = doc(db, providerConfigModel.collection, providerId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const providerConfig = {
        id: docSnap.id,
        ...docSnap.data()
      };
      
      console.log('✅ Provider configuration found:', providerConfig.providerName);
      return {
        success: true,
        data: providerConfig
      };
    } else {
      console.log('❌ Provider configuration not found');
      return {
        success: false,
        error: 'Provider configuration not found'
      };
    }
    
  } catch (error) {
    console.error('❌ Error fetching provider configuration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get provider configuration by code
export const getProviderConfigByCode = async (providerCode) => {
  try {
    console.log('📖 Fetching provider configuration by code:', providerCode);
    
    const q = query(
      collection(db, providerConfigModel.collection),
      where('providerCode', '==', providerCode),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      const providerConfig = {
        id: doc.id,
        ...doc.data()
      };
      
      console.log('✅ Provider configuration found:', providerConfig.providerName);
      return {
        success: true,
        data: providerConfig
      };
    } else {
      console.log('❌ Provider configuration not found');
      return {
        success: false,
        error: 'Provider configuration not found'
      };
    }
    
  } catch (error) {
    console.error('❌ Error fetching provider configuration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get active provider configurations
export const getActiveProviderConfigs = async () => {
  try {
    console.log('📖 Fetching active provider configurations...');
    
    const q = query(
      collection(db, providerConfigModel.collection),
      where('settings.isActive', '==', true),
      where('status', '==', 'active'),
      orderBy('settings.priority', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const providerConfigs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('✅ Fetched active provider configurations:', providerConfigs.length);
    return {
      success: true,
      data: providerConfigs
    };
    
  } catch (error) {
    console.error('❌ Error fetching active provider configurations:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Update provider configuration
export const updateProviderConfigDocument = async (providerId, updateData, userId) => {
  try {
    console.log('🔄 Updating provider configuration:', providerId);
    console.log('👤 User ID:', userId);
    console.log('📝 Update data:', updateData);
    
    const updatePayload = {
      ...updateData,
      updatedAt: serverTimestamp(),
      updatedBy: userId
    };
    
    // Validate the updated data
    const currentDoc = await getProviderConfigById(providerId);
    if (!currentDoc.success) {
      throw new Error('Provider configuration not found');
    }
    
    const updatedConfig = {
      ...currentDoc.data,
      ...updatePayload
    };
    
    const validation = validateProviderConfig(updatedConfig);
    if (!validation.isValid) {
      console.log('❌ Validation failed:', validation.errors);
      throw new Error(`Validation failed: ${Object.values(validation.errors).join(', ')}`);
    }
    
    console.log('💾 Updating Firestore document...');
    const docRef = doc(db, providerConfigModel.collection, providerId);
    await updateDoc(docRef, updatePayload);
    
    console.log('✅ Provider configuration updated successfully');
    return {
      success: true,
      data: updatedConfig
    };
    
  } catch (error) {
    console.error('❌ Error updating provider configuration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete provider configuration
export const deleteProviderConfigDocument = async (providerId) => {
  try {
    console.log('🗑️ Deleting provider configuration:', providerId);
    
    const docRef = doc(db, providerConfigModel.collection, providerId);
    await deleteDoc(docRef);
    
    console.log('✅ Provider configuration deleted successfully');
    return {
      success: true
    };
    
  } catch (error) {
    console.error('❌ Error deleting provider configuration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Initialize predefined providers
export const initializePredefinedProviders = async (userId) => {
  try {
    console.log('🚀 Initializing predefined providers...');
    
    const existingProviders = await getAllProviderConfigs();
    const existingProviderCodes = existingProviders.success ? 
      existingProviders.data.map(provider => provider.providerCode) : [];
    
    const providersToCreate = predefinedProviders.filter(provider => 
      !existingProviderCodes.includes(provider.providerCode)
    );
    
    console.log(`📊 Found ${providersToCreate.length} new providers to create`);
    
    const results = [];
    for (const providerData of providersToCreate) {
      const result = await createProviderConfigDocument(providerData, userId);
      results.push(result);
    }
    
    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Successfully created ${successCount} provider configurations`);
    
    return {
      success: true,
      created: successCount,
      total: providersToCreate.length,
      results
    };
    
  } catch (error) {
    console.error('❌ Error initializing predefined providers:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Update provider statistics
export const updateProviderStats = async (providerId, statsUpdate) => {
  try {
    console.log('📊 Updating provider statistics:', providerId);
    console.log('📈 Stats update:', statsUpdate);
    
    const docRef = doc(db, providerConfigModel.collection, providerId);
    const currentDoc = await getDoc(docRef);
    
    if (!currentDoc.exists()) {
      throw new Error('Provider configuration not found');
    }
    
    const currentData = currentDoc.data();
    const updatedStats = {
      ...currentData.stats,
      ...statsUpdate,
      lastRequestTime: new Date().toISOString()
    };
    
    await updateDoc(docRef, {
      stats: updatedStats,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Provider statistics updated successfully');
    return {
      success: true,
      data: updatedStats
    };
    
  } catch (error) {
    console.error('❌ Error updating provider statistics:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Test provider API connection
export const testProviderConnection = async (providerId) => {
  try {
    console.log('🧪 Testing provider connection:', providerId);
    
    const providerResult = await getProviderConfigById(providerId);
    if (!providerResult.success) {
      throw new Error('Provider not found');
    }
    
    const provider = providerResult.data;
    
    // Mock API test (in production, you would make actual API calls)
    const testResult = {
      success: true,
      responseTime: Math.random() * 1000 + 500, // Mock response time
      status: 'active',
      message: 'Connection test successful'
    };
    
    // Update provider status based on test result
    const statusUpdate = testResult.success ? 'active' : 'error';
    await updateProviderConfigDocument(providerId, {
      status: statusUpdate,
      lastTested: new Date().toISOString(),
      lastError: testResult.success ? null : testResult.message
    }, 'system');
    
    console.log('✅ Provider connection test completed');
    return {
      success: true,
      data: testResult
    };
    
  } catch (error) {
    console.error('❌ Error testing provider connection:', error);
    
    // Update provider status to error
    await updateProviderConfigDocument(providerId, {
      status: 'error',
      lastError: error.message
    }, 'system');
    
    return {
      success: false,
      error: error.message
    };
  }
};

// Toggle provider active status
export const toggleProviderStatus = async (providerId, isActive, userId) => {
  try {
    console.log('🔄 Toggling provider status:', providerId, 'to', isActive);
    
    const updateData = {
      'settings.isActive': isActive,
      status: isActive ? 'active' : 'inactive'
    };
    
    const result = await updateProviderConfigDocument(providerId, updateData, userId);
    
    if (result.success) {
      console.log('✅ Provider status toggled successfully');
    }
    
    return result;
    
  } catch (error) {
    console.error('❌ Error toggling provider status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get provider statistics summary
export const getProviderStatisticsSummary = async () => {
  try {
    console.log('📊 Fetching provider statistics summary...');
    
    const result = await getAllProviderConfigs();
    if (!result.success) {
      throw new Error(result.error);
    }
    
    const statistics = getProviderStatistics(result.data);
    
    console.log('✅ Provider statistics calculated:', statistics);
    return {
      success: true,
      data: statistics
    };
    
  } catch (error) {
    console.error('❌ Error calculating provider statistics:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Bulk update providers
export const bulkUpdateProviders = async (providerIds, updateData, userId) => {
  try {
    console.log('🔄 Bulk updating providers:', providerIds.length);
    
    const results = [];
    for (const providerId of providerIds) {
      const result = await updateProviderConfigDocument(providerId, updateData, userId);
      results.push({ id: providerId, ...result });
    }
    
    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Bulk update completed: ${successCount}/${providerIds.length} successful`);
    
    return {
      success: true,
      results,
      successCount,
      totalCount: providerIds.length
    };
    
  } catch (error) {
    console.error('❌ Error in bulk update:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

